/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight, Globe2, Bot, Image as ImageIcon, Trophy, Users, Wallet,
  Activity, Cake, GraduationCap, Newspaper, Handshake, Headphones,
  Sparkles, Zap, Brain, Database, Shield, Cloud, RefreshCw, BarChart3,
  Calendar, AlertTriangle, Clock, Target, ChevronDown, MessageCircle,
  Check, ArrowUpRight, PlayCircle, Star, Phone,
} from "lucide-react";
import MarketingLayout, { SmartImg } from "../../components/marketing/MarketingLayout";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const IMG = "/marketing-v2";

/* ============ SHARED HELPERS ============ */
const fadeUp = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-80px", once: true });
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "show" : "hidden"}
      variants={fadeUp}
      custom={delay}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const SectionNumber = ({ n, label }) => (
  <div className="flex items-center gap-3 mb-5" data-testid={`section-over-${n}`}>
    <span className="mkt-overline-num">{String(n).padStart(2, "0")}</span>
    <span className="h-[1px] w-8 bg-dr-yellow/50" />
    <span className="font-over text-white/60">{label}</span>
  </div>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative px-6 lg:px-10 py-24 md:py-32 ${className}`}>
    <div className="max-w-[1400px] mx-auto">{children}</div>
  </section>
);

/* ============ COUNTER ============ */
const Counter = ({ to, suffix = "", duration = 1.6 }) => {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  useEffect(() => {
    if (!inView) return;
    let raf, start;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / (duration * 1000), 1);
      setVal(Math.floor(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);
  return (
    <span ref={ref} className="tabular-nums">
      {val.toLocaleString("tr-TR")}{suffix}
    </span>
  );
};

/* ============ 1. HERO ============ */
const Hero = () => {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.25], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.4]);

  return (
    <section className="relative min-h-screen overflow-hidden mkt-glow-yellow mkt-grain pt-24 pb-16 lg:pt-28" data-testid="hero-section">
      {/* Parallax bg image */}
      <motion.div
        style={{ y, opacity }}
        className="absolute inset-0 -z-10"
      >
        <SmartImg
          src={`${IMG}/hero-stadium.png`}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-dr-black/40 via-dr-black/80 to-dr-black" />
      </motion.div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[82vh]">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-dr-yellow/30 bg-dr-yellow/5 mb-8">
              <Zap size={14} className="text-dr-yellow" />
              <span className="font-over text-dr-yellow">FUTBOL KULÜPLERİ İÇİN YAPAY ZEKA DESTEKLİ DİJİTAL YÖNETİM</span>
            </div>
          </Reveal>

          <h1 className="font-hero uppercase text-[42px] sm:text-[58px] md:text-[68px] lg:text-[80px] xl:text-[92px] leading-[0.92] mb-8 break-words">
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.10, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="block"
            >
              KULÜBÜNÜZÜ
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.18, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="block text-dr-yellow"
            >
              YÖNETMENİN
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.26, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="block whitespace-nowrap"
            >
              YENİ ÇAĞI
            </motion.span>
          </h1>

          <Reveal delay={5}>
            <p className="text-lg md:text-xl text-white/75 max-w-xl leading-relaxed mb-10">
              Sosyal medyadan mali kasa'ya, maç analizinden yoklamaya —{" "}
              <span className="text-white font-semibold">tek panelden</span>,{" "}
              <span className="text-white font-semibold">tek tıkla</span>,{" "}
              <span className="text-dr-yellow font-semibold">yapay zeka gücüyle</span>.
              BAŞKAN'ın ve Yöneticilerin rüya gördüğü sistem artık gerçek.
            </p>
          </Reveal>

          <Reveal delay={6}>
            <div className="flex flex-wrap gap-4 mb-10">
              <Link to="/uye-ol" className="mkt-btn-primary" data-testid="hero-cta-demo">
                Ücretsiz Üye Ol <ArrowRight size={18} strokeWidth={3} />
              </Link>
              <Link to="/ozellikler" className="mkt-btn-ghost" data-testid="hero-cta-features">
                Özellikleri Gez
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <Reveal delay={2}>
            <div className="relative">
              <div className="absolute -inset-8 bg-dr-yellow/20 blur-3xl rounded-full" />
              <SmartImg
                src={`${IMG}/hero-stadium.png`}
                alt="DR AI Futbol — Stadium with AI overlay"
                className="relative w-full rounded-2xl shadow-[0_40px_120px_-20px_rgba(0,0,0,0.8)] border border-white/5"
                loading="eager"
              />
              {/* floating badges */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1, duration: 0.6 }}
                className="absolute -left-4 top-8 bg-dr-ink-1 border border-white/10 px-4 py-2.5 rounded-xl shadow-xl"
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-2 h-2 bg-dr-success rounded-full animate-pulse" />
                  <span className="text-xs text-white/90 font-semibold">LİVANESPOR · 3. sıra</span>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                className="absolute -right-4 bottom-12 bg-dr-yellow text-dr-black px-4 py-2.5 rounded-xl shadow-xl"
              >
                <div className="flex items-center gap-2">
                  <Sparkles size={14} />
                  <span className="text-xs font-black uppercase">DR AI Raporu Hazır</span>
                </div>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Ticker marquee */}
      <div className="relative mt-10 mkt-marquee-mask overflow-hidden">
        <div className="flex mkt-ticker whitespace-nowrap">
          {[...Array(2)].map((_, rep) => (
            <div key={rep} className="flex items-center gap-12 shrink-0 px-6">
              {[
                "LİVANESPOR", "BURSASPOR AMATÖR", "GENÇLİKSPOR", "MUDANYASPOR",
                "ELBEYLİ ÜZÜMSPOR", "ANADOLUSPOR", "YILDIZ FK", "KARTAL GENÇLİK",
                "DENİZ YILDIZLARI", "VEFA SPOR", "BAL KULÜBÜ", "ŞAHİNLER SPOR",
              ].map((name) => (
                <span key={`${rep}-${name}`} className="font-hero text-white/25 text-2xl tracking-wider">
                  {name} <span className="text-dr-yellow/40 mx-6">◆</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============ 2. "BAŞKAN MASASINA GELEN PAKET" — 12 KART ============ */
const BENEFITS = [
  { icon: Globe2,      t: "Kurumsal Web Siteniz",    d: "livanespor.org gibi özel domain + profesyonel tasarım" },
  { icon: Bot,         t: "AI Maç Analiz Sistemi",   d: "Rakibi tanıyın, maçtan önce kazanın. 6 bölümlük rapor." },
  { icon: ImageIcon,   t: "9 Instagram Şablonu",     d: "Her maç, her olay için AI görsel üretimi" },
  { icon: Trophy,      t: "Otomatik Puan Durumu",    d: "Resmi lig verisinden çekilir · her Pazar 00:00" },
  { icon: Users,       t: "Kadro & Transfer",        d: "Sözleşmeler, bonuslar, primler eksiksiz" },
  { icon: Wallet,      t: "Kulüp Maliyesi (Kasa)",   d: "Aylık net gelir/gider tek bakışta" },
  { icon: Activity,    t: "Antrenman & Yoklama",     d: "Kim geldi, kim gelmedi, neden?" },
  { icon: Cake,        t: "Doğum Günü Takibi",       d: "Hiçbir oyuncu unutulmaz + AI görseli" },
  { icon: GraduationCap,t:"Akademi Yönetimi",        d: "Grup, başvuru, ödeme, antrenman" },
  { icon: Newspaper,   t: "Blog & Haber Sistemi",    d: "Taraftarınızla sürekli bağ" },
  { icon: Handshake,   t: "Sponsor Yönetimi",        d: "Anlaşmalar, tahsilatlar, tier'lar" },
  { icon: Headphones,  t: "7/24 Teknik Destek",      d: "WhatsApp + telefon · her an yanınızda" },
];

const BenefitsSection = () => (
  <Section id="paket">
    <div className="max-w-4xl mb-16">
      <SectionNumber n={1} label="PAKET İÇERİĞİ" />
      <Reveal>
        <h2 className="mkt-h2 mb-6">
          BAŞKAN,<br />
          İŞTE <span className="text-dr-yellow">MASANIZA</span><br />
          GELEN PAKET
        </h2>
      </Reveal>
      <Reveal delay={1}>
        <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
          Sözleşmeyi imzaladığınız gün, kulübünüz aşağıdaki <span className="text-white font-semibold">her şeye anında sahip</span> olur.
          Tek panel, tek fatura, sınırsız kulüp medyası.
        </p>
      </Reveal>
    </div>

    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {BENEFITS.map((b, i) => (
        <Reveal key={b.t} delay={i % 3}>
          <div className="mkt-card rounded-2xl p-7 h-full group cursor-pointer" data-testid={`benefit-${i}`}>
            <div className="flex items-start justify-between mb-6">
              <div className="w-12 h-12 rounded-xl bg-dr-yellow/10 border border-dr-yellow/20 flex items-center justify-center group-hover:bg-dr-yellow group-hover:scale-110 transition-all duration-300">
                <b.icon size={22} className="text-dr-yellow group-hover:text-dr-black transition-colors" strokeWidth={2} />
              </div>
              <span className="font-over text-white/30">{String(i + 1).padStart(2, "0")}</span>
            </div>
            <h3 className="font-hero text-xl uppercase mb-2">{b.t}</h3>
            <p className="text-sm text-white/60 leading-relaxed mb-4">{b.d}</p>
            <ArrowUpRight size={16} className="text-dr-yellow opacity-0 group-hover:opacity-100 transition" />
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

/* ============ 3. DASHBOARD TANITIMI ============ */
const DashWidgets = [
  ["📈", "Lig Durumu Hero Kartı", "Sıra, puan, son 5 maç rozeti"],
  ["💰", "Bu Ay Kasa", "Net gelir/gider + geçen ay karşılaştırma"],
  ["📋", "Biten Sözleşmeler", "90 gün erken uyarı — kritik alarm"],
  ["🧠", "AI Rakip Analizi", "Bir sonraki rakibe DR AI Engine raporu"],
  ["🔄", "Lig Verisi Sync", "Son otomatik veri çekim zamanı"],
  ["⚽", "Yaklaşan Maçlar", "Tarih, saat, saha, hızlı aksiyon"],
  ["🎯", "Antrenman Özeti", "%X devamlılık, günlük breakdown"],
  ["🏆", "Devamlılık Şampiyonları", "Top 5 oyuncu"],
  ["🚫", "En Çok Gelmeyenler", "Son 30 gün uyarı listesi"],
  ["🎂", "Yaklaşan Doğum Günleri", "30 günlük takvim"],
  ["📬", "Okunmamış Mesajlar", "Web sitesi iletişim"],
  ["📝", "Bekleyen Başvurular", "Akademi başvuru yönetimi"],
  ["⚡", "Hızlı Aksiyonlar", "Yeni maç · AI görsel · sync tek tık"],
];

const DashboardSection = () => (
  <Section id="dashboard" className="bg-dr-ink-1/30">
    <div className="grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <SectionNumber n={2} label="ANLIK PANEL GÖRÜNÜMÜ" />
        <Reveal>
          <h2 className="mkt-h2 mb-6">
            BAŞKAN'IN SABAH<br />
            KAHVESİYLE GÖRDÜĞÜ<br />
            <span className="text-dr-yellow">TEK EKRAN</span>
          </h2>
        </Reveal>
        <Reveal delay={1}>
          <p className="text-lg text-white/70 leading-relaxed mb-10">
            Paneli her açtığınızda; kasa, sözleşmeler, yaklaşan maç, rakip analizi, antrenman durumu — hepsi tek bakışta.
            Dağınık dosyalar, kaybolan WhatsApp mesajları, unutulan toplantılar <span className="text-dr-risk font-semibold">artık yok.</span>
          </p>
        </Reveal>
        <div className="grid grid-cols-1 gap-2 max-h-[420px] overflow-y-auto pr-2 custom-scroll">
          {DashWidgets.map(([emoji, title, desc], i) => (
            <Reveal key={title} delay={i % 4}>
              <div className="flex items-start gap-4 p-3 rounded-lg hover:bg-white/5 transition group" data-testid={`widget-${i}`}>
                <span className="text-xl mt-0.5 grayscale group-hover:grayscale-0 transition">{emoji}</span>
                <div className="min-w-0">
                  <div className="text-white font-semibold text-sm">{title}</div>
                  <div className="text-white/50 text-xs mt-0.5">{desc}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal delay={2}>
        <div className="relative">
          <div className="absolute -inset-12 bg-dr-yellow/10 blur-3xl rounded-full" />
          <SmartImg src={`${IMG}/dashboard-mockup.png`} alt="DR AI Futbol Dashboard" className="relative w-full rounded-2xl border border-white/10 shadow-2xl" />
          <div className="absolute -top-4 -right-4 bg-dr-yellow text-dr-black px-3 py-1.5 rounded-lg font-over">CANLI</div>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ============ 4. AI MAÇ ANALİZİ (EN WOW) ============ */
const AIAnalysisSection = () => {
  const [open, setOpen] = useState(false);
  return (
    <Section id="ai-analiz" className="overflow-hidden">
      <div className="absolute top-20 left-0 w-[500px] h-[500px] bg-dr-yellow/5 blur-[120px] rounded-full -z-10" />
      <div className="grid lg:grid-cols-2 gap-16 items-start">
        <div>
          <SectionNumber n={3} label="DR AI FUTBOL — MAÇ ÖNÜ ANALİZ" />
          <Reveal>
            <h2 className="mkt-h2 mb-4">
              CUMARTESİ RAKİBİ<br />
              <span className="text-dr-yellow">TANIYOR MUSUNUZ?</span>
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="text-2xl md:text-3xl text-white/90 font-hero uppercase mb-8">
              DR AI FUTBOL bunu <span className="text-dr-yellow">30 saniyede</span> yapar.
            </p>
          </Reveal>
          <Reveal delay={2}>
            <div className="space-y-4 text-white/70 text-[15px] leading-relaxed mb-8">
              <p>Yönetim kuruluna oturduğunuzda, masaya koyduğunuz rapor sadece "rakip iyi oynuyor" demez. Şunları söyler:</p>
              <ul className="space-y-2.5">
                {[
                  "Rakibin son 10 maçtaki form serisi (GGMBG gibi)",
                  "Ev ve deplasman performansının kırılımı",
                  "Rakibin en golcü 3 oyuncusu — isim, numara, gol sayısı, maç başı oranı",
                  "Savunma kırılganlığı yüzdesi (clean sheet oranı)",
                  "Otomatik TEHLİKE UYARILARI (rakam bazlı, ezberli değil)",
                  "İlk 11 önerisi + gerçekçi skor tahmini",
                  "MOTM (Man of the Match — Maçın Adamı) adayımız isimle birlikte",
                  "Geçmiş karşılaşma verisi + psikolojik yorum",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <Check size={18} className="text-dr-yellow shrink-0 mt-0.5" strokeWidth={3} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="pt-2">
                Tüm bunları <strong className="text-white">DR AI Engine</strong> motorundan özel verileri harmanlayarak
                tamamen size özel bilgileri <strong className="text-dr-yellow">30 saniyede</strong> 6 bölümlük profesyonel rapor olarak sunar.
              </p>
            </div>
          </Reveal>
          <Reveal delay={3}>
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                [Clock, "30 saniye"],
                [Target, "Rakam bazlı"],
                [Database, "Canlı Lig Verisi"],
                [Brain, "DR AI Engine"],
              ].map(([Icon, lbl]) => (
                <span key={lbl} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] text-[13px] text-white/80">
                  <Icon size={14} className="text-dr-yellow" strokeWidth={2.5} />
                  {lbl}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={4}>
            <button
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-3 text-dr-yellow font-over hover:text-dr-yellow-hi transition"
              data-testid="ai-analiz-accordion"
            >
              {open ? "Teknik detayları gizle" : "Teknik detaylar"}
              <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={3} />
            </button>
            <AnimatePresence>
              {open && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-5 p-5 bg-dr-ink-2 border border-dr-yellow/20 rounded-xl text-sm text-white/75 leading-relaxed">
                    Her rapor 6 bölüm içerir: <strong className="text-dr-yellow">1-Genel Değerlendirme</strong> · <strong className="text-dr-yellow">2-Bizim Takım Form Analizi</strong> · <strong className="text-dr-yellow">3-Rakip Analizi</strong> · <strong className="text-dr-yellow">4-Geçmiş Karşılaşmalar</strong> · <strong className="text-dr-yellow">5-Tahmin & Öneri</strong> · <strong className="text-dr-yellow">6-Özet Uyarılar</strong>.
                    Kulüp başkanına özel sunuma hazır <strong className="text-white">PDF</strong> hazırlanıp indirilebilir.
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </Reveal>
        </div>

        <Reveal delay={3}>
          <div className="relative lg:sticky lg:top-24">
            <div className="absolute -inset-8 bg-dr-yellow/15 blur-3xl rounded-full" />
            <SmartImg src={`${IMG}/ai-analiz-ipad.png`} alt="AI Maç Analiz Raporu" className="relative w-full rounded-2xl border border-white/10 shadow-2xl" />
          </div>
        </Reveal>
      </div>
    </Section>
  );
};

/* ============ 5. 9 INSTAGRAM ŞABLONU ============ */
const TEMPLATES = [
  ["Maç Haftası Duyuru", "insta-01-match-week.png"],
  ["İlk 11 Kadro", "insta-02-lineup.png"],
  ["Galibiyet Skoru", "insta-03-win-score.png"],
  ["Mağlubiyet (Söz Veriyoruz)", "insta-04-defeat.png"],
  ["Oyuncu Doğum Günü", "insta-05-birthday.png"],
  ["Transfer Duyurusu", "insta-06-transfer.png"],
  ["Gol Atan Oyuncu", "insta-07-goal.png"],
  ["Puan Durumu", "insta-08-standings.png"],
  ["Antrenman Kadrosu", "insta-09-training.png"],
];
const TemplatesSection = () => {
  const [lightbox, setLightbox] = useState(null);
  return (
    <Section id="sablonlar" className="bg-dr-ink-1/30">
      <div className="max-w-4xl mb-16">
        <SectionNumber n={4} label="9 AI INSTAGRAM ŞABLONU" />
        <Reveal>
          <h2 className="mkt-h2 mb-6">
            INSTAGRAM'A MAÇ GÜNÜ<br />
            <span className="text-dr-yellow">NE KOYACAĞIZ?</span> DERDİ BİTTİ
          </h2>
        </Reveal>
        <Reveal delay={1}>
          <p className="text-lg text-white/70 leading-relaxed">
            Tek tık. AI sizin renklerinizi, logonuzu, DR AI FUTBOL imzasını otomatik kullanır.
            9 profesyonel şablon, sınırsız varyasyon.
          </p>
        </Reveal>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TEMPLATES.map(([title, file], i) => (
          <Reveal key={file} delay={i % 3}>
            <button
              onClick={() => setLightbox({ title, file, idx: i })}
              className="relative group w-full overflow-hidden rounded-2xl border border-white/10 hover:border-dr-yellow/50 transition-all"
              data-testid={`template-${i}`}
            >
              <SmartImg src={`${IMG}/${file}`} alt={title} className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-dr-black via-dr-black/20 to-transparent opacity-60 group-hover:opacity-90 transition" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-left">
                <div className="font-over text-dr-yellow mb-1">ŞABLON {String(i + 1).padStart(2, "0")} / 09</div>
                <div className="font-hero text-lg uppercase">{title}</div>
              </div>
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition">
                <div className="w-9 h-9 bg-dr-yellow text-dr-black rounded-full flex items-center justify-center">
                  <PlayCircle size={18} strokeWidth={2.5} />
                </div>
              </div>
            </button>
          </Reveal>
        ))}
      </div>

      <Reveal delay={2}>
        <p className="text-center text-white/60 mt-10 text-sm max-w-2xl mx-auto">
          Her şablonda kulübünüzün <strong className="text-white">logosu</strong>, <strong className="text-white">Instagram kullanıcı adı</strong> ve
          <strong className="text-white"> web site adresi</strong> imzası otomatik eklenir.
          Siz sadece konu seçersiniz — <strong className="text-dr-yellow">DR AI</strong> görseli kulübünüze özel üretir.
          <span className="block mt-2 text-dr-yellow font-semibold">Diğer kulüplere ASLA benzemez.</span>
        </p>
      </Reveal>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[100] bg-dr-black/95 backdrop-blur-xl flex items-center justify-center p-4 cursor-pointer"
            data-testid="template-lightbox"
          >
            <motion.img
              initial={{ scale: 0.9 }} animate={{ scale: 1 }}
              src={`${IMG}/${lightbox.file}`} alt={lightbox.title}
              className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Section>
  );
};

/* ============ 6. KASA ============ */
const KasaSection = () => (
  <Section id="kasa">
    <div className="grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <SectionNumber n={5} label="KULÜP MALİYESİ" />
        <Reveal>
          <h2 className="mkt-h2 mb-6">
            KAFANIZDAKİ #1 YÜK:<br />
            <span className="text-dr-yellow">KASA</span>
          </h2>
        </Reveal>
        <Reveal delay={1}>
          <p className="text-lg text-white/70 leading-relaxed mb-8">
            Defter tutmaya gerek yok. Kategori seç, tutar gir, bitti.
            <span className="text-white font-semibold"> Başkanın kafe defteri</span> artık dijital.
          </p>
        </Reveal>
        <div className="space-y-5">
          <Reveal delay={2}>
            <div className="mkt-card p-5 rounded-xl">
              <div className="font-over text-dr-success mb-3">8 GELİR KATEGORİSİ</div>
              <div className="flex flex-wrap gap-2">
                {["Sponsorluk", "Akademi Aidatı", "Forma Satışı", "Bilet", "Bağış", "Yayın Hakkı", "TFF Desteği", "Diğer"].map((c) => (
                  <span key={c} className="text-xs px-3 py-1 rounded-full bg-dr-success/10 text-dr-success border border-dr-success/20">{c}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={3}>
            <div className="mkt-card p-5 rounded-xl">
              <div className="font-over text-dr-risk mb-3">8 GİDER KATEGORİSİ</div>
              <div className="flex flex-wrap gap-2">
                {["Maaş", "Saha Kirası", "Müsabaka Giderleri", "Yakıt", "Malzeme", "Yemek", "Sağlık", "Diğer"].map((c) => (
                  <span key={c} className="text-xs px-3 py-1 rounded-full bg-dr-risk/10 text-dr-risk border border-dr-risk/20">{c}</span>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={4}>
            <div className="mkt-card p-6 rounded-xl">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-over text-white/50">BU AY</div>
                  <div className="font-hero text-2xl text-dr-success mt-1">+₺28.500</div>
                </div>
                <div className="border-x border-white/5">
                  <div className="font-over text-white/50">GEÇEN AY</div>
                  <div className="font-hero text-2xl text-white/80 mt-1">+₺19.200</div>
                </div>
                <div>
                  <div className="font-over text-white/50">BEKLENEN</div>
                  <div className="font-hero text-2xl text-dr-warn mt-1">₺8.000</div>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-2 text-sm p-3 bg-dr-risk/10 border border-dr-risk/20 rounded-lg">
                <AlertTriangle size={16} className="text-dr-risk shrink-0" />
                <span className="text-dr-risk">3 sponsor ödemesi 30+ gün gecikmiş</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      <Reveal delay={3}>
        <div className="relative">
          <div className="absolute -inset-8 bg-dr-warn/10 blur-3xl rounded-full" />
          <SmartImg src={`${IMG}/kasa-desk.png`} alt="Kulüp Maliyesi" className="relative w-full rounded-2xl border border-white/10 shadow-2xl" />
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ============ 7. SÖZLEŞME ============ */
const CONTRACT_CARDS = [
  ["Sözleşme CRUD", "Tip · tarih · tutar · bonservis · bonus koşulları"],
  ["90 Gün Erken Uyarı", "Dashboard'da kırmızı alarm, kimse kaçmaz"],
  ["Transfer Geçmişi", "Geldiği kulüp, bonservis, tarihler"],
  ["8 Ödeme Tipi", "Bonservis · Taksit · Maaş · Maç Primi · Galibiyet · İmza · Bonus · Diğer"],
  ["Ek Ödeme Rozeti", "'600K anlaşıldık, 750K ödendi' → +₺150.000 rozeti"],
  ["Oyuncu Finansal Özet", "Sözleşme vs toplam ödenen vs kalan borç"],
];
const ContractSection = () => (
  <Section id="sozlesme" className="bg-dr-ink-1/30">
    <div className="max-w-4xl mb-14">
      <SectionNumber n={6} label="SÖZLEŞME & ÖDEME" />
      <Reveal>
        <h2 className="mkt-h2 mb-6">
          "SÖZLEŞMESİ BİTTİ,<br />
          ADAM <span className="text-dr-risk">BEDAVA GİTTİ</span>"<br />
          SENARYOSU BİTTİ
        </h2>
      </Reveal>
      <Reveal delay={1}>
        <p className="text-lg text-white/70 leading-relaxed">
          Profesyonel kulüpler bile karıştırıyor. Siz karıştırmayın. Her oyuncu, her kuruş, her tarih — takipte.
        </p>
      </Reveal>
    </div>
    <div className="grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
        {CONTRACT_CARDS.map((c, i) => (
          <Reveal key={c[0]} delay={i % 2}>
            <div className="mkt-card rounded-xl p-5 h-full" data-testid={`contract-${i}`}>
              <div className="font-over text-dr-yellow mb-2">{String(i + 1).padStart(2, "0")}</div>
              <div className="font-hero text-base uppercase mb-1.5">{c[0]}</div>
              <div className="text-sm text-white/60 leading-relaxed">{c[1]}</div>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={2} className="lg:col-span-5">
        <SmartImg src={`${IMG}/contract-split.png`} alt="Sözleşme & Ödeme" className="w-full rounded-2xl border border-white/10 shadow-2xl" />
      </Reveal>
    </div>
  </Section>
);

/* ============ 8. ANTRENMAN ============ */
const TRAIN_CHECKS = [
  "Antrenman takvimi: A Takım + U13/U14/U15/U17/U19",
  "Grup bazlı oyuncu filtresi (U17 = ≤17 yaş otomatik)",
  "Default: Herkes 'Geldi' — sadece istisnaları işaretle",
  "'Gelmedi' → sebep input'u zorunlu (boş kabul etmez)",
  "Bu hafta devamlılık yüzdesi — günlük çubuk grafik",
  "Devamlılık şampiyonları (top 5 %100)",
  "En çok gelmeyenler (son 30 gün uyarı)",
  "Oyuncu profilinde son 10 antrenman geçmişi + ortalama %",
];
const TrainingSection = () => (
  <Section id="antrenman">
    <div className="grid lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="relative">
          <SmartImg src={`${IMG}/training-attendance.png`} alt="Antrenman & Yoklama" className="w-full rounded-2xl border border-white/10 shadow-2xl" />
          <div className="absolute top-6 left-6 bg-dr-black/90 backdrop-blur border border-dr-yellow/30 px-4 py-2.5 rounded-xl">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-dr-yellow rounded-full animate-pulse" />
              <span className="font-over text-dr-yellow">CANLI · 14 / 18 GELDİ</span>
            </div>
          </div>
        </div>
      </Reveal>
      <div>
        <SectionNumber n={7} label="ANTRENMAN & YOKLAMA" />
        <Reveal>
          <h2 className="mkt-h2 mb-6">
            KİM GELDİ,<br />
            KİM GELMEDİ,<br />
            <span className="text-dr-yellow">NEDEN?</span>
          </h2>
        </Reveal>
        <Reveal delay={1}>
          <p className="text-lg text-white/70 leading-relaxed mb-8">
            Hoca WhatsApp'ta isim sormaktan kurtulur.
            Yoklama ekranı = <span className="text-white font-bold">30 saniye.</span>
          </p>
        </Reveal>
        <ul className="space-y-3">
          {TRAIN_CHECKS.map((t, i) => (
            <Reveal key={t} delay={i % 4}>
              <li className="flex gap-3 text-[15px] text-white/80 leading-relaxed">
                <Check size={18} className="text-dr-yellow shrink-0 mt-1" strokeWidth={3} />
                <span>{t}</span>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>
  </Section>
);

/* ============ 9. AKADEMİ + 10. MACKOLIK ============ */
const AcademyMackolikSection = () => (
  <Section id="akademi-mackolik" className="bg-dr-ink-1/30">
    <div className="grid lg:grid-cols-2 gap-12">
      <Reveal>
        <div className="mkt-card rounded-3xl overflow-hidden h-full flex flex-col">
          <SmartImg src={`${IMG}/academy-kids.png`} alt="Akademi" className="w-full aspect-[16/10] object-cover" />
          <div className="p-8 lg:p-10 flex-1">
            <SectionNumber n={8} label="AKADEMİ YÖNETİMİ" />
            <h3 className="font-hero text-3xl uppercase mb-4">
              GELECEK YILDIZLAR<br />
              <span className="text-dr-yellow">SİZİN SAHANIZDA</span>
            </h3>
            <p className="text-white/70 leading-relaxed mb-5">
              Başvurudan aidat takibine, grup yönetiminden antrenman takvimine — tek çatı altında.
            </p>
            <ul className="space-y-2.5 text-[14px] text-white/75">
              {[
                "Akademi grupları (yaş, antrenör, tempo)",
                "Ayrı antrenman takvimi",
                "Başvuru formu (web → panel otomatik)",
                "Başvuru durumu: Beklemede / Kabul / Red",
                "Grup bazlı oyuncu listesi + hafta özeti",
              ].map((x) => (
                <li key={x} className="flex gap-2.5">
                  <Check size={16} className="text-dr-yellow shrink-0 mt-0.5" strokeWidth={3} /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <Reveal delay={1}>
        <div className="mkt-card rounded-3xl overflow-hidden h-full flex flex-col">
          <SmartImg src={`${IMG}/mackolik-data.png`} alt="Lig Verisi Sync" className="w-full aspect-[16/10] object-cover" />
          <div className="p-8 lg:p-10 flex-1">
            <SectionNumber n={9} label="MACKOLİK SENKRONİZASYON" />
            <h3 className="font-hero text-3xl uppercase mb-4">
              PUAN TABLOSUNU<br />
              EL İLE Mİ? <span className="text-dr-yellow">HAYIR.</span>
            </h3>
            <p className="text-white/70 leading-relaxed mb-5">
              Her Pazar gecesi otomatik çekilir. İstediğiniz zaman manuel de çekebilirsiniz.
            </p>
            <ul className="space-y-2.5 text-[14px] text-white/75">
              {[
                "Puan durumu (sıra, G/B/M, averaj)",
                "Fikstür (geçmiş + gelecek, status auto)",
                "Kadro + gol/asist istatistikleri",
                "Oyuncu fotoğraflarını panelden yükleyin",
                "Rakip kulüp logoları",
                "Zamanlanmış haftalık sync (APScheduler)",
              ].map((x) => (
                <li key={x} className="flex gap-2.5">
                  <Check size={16} className="text-dr-yellow shrink-0 mt-0.5" strokeWidth={3} /> {x}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </div>
  </Section>
);

/* ============ 11. BLOG · SPONSOR · HERO · FOTO ============ */
const MINI = [
  ["Newspaper", "Blog & Haber", "Kategori · öne çıkan foto · SEO slug · taslak/yayın. Web sitenizde otomatik listelenir."],
  ["Handshake", "Sponsor Yönetimi", "Logo · tier (Main/Gold/Silver) · web linki · anlaşma süresi. Footer'a otomatik yansır."],
  ["ImageIcon", "Hero Slider", "Ana sayfa slider'ına görsel + CTA ekle. Kampanya, duyuru, yeni transfer."],
  ["Camera", "Takım Fotoğrafları", "Sezonluk kare fotoğrafı yükle, galeriye yansır."],
];
const MiniGridSection = () => (
  <Section id="blog-sponsor">
    <div className="max-w-4xl mb-12">
      <SectionNumber n={10} label="WEB SİTESİNE YANSIYAN MODÜLLER" />
      <Reveal>
        <h2 className="mkt-h2 mb-4">
          PANEL'DE DEĞİŞTİR,<br />
          SİTEDE <span className="text-dr-yellow">ANINDA YAYINLA</span>
        </h2>
      </Reveal>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {MINI.map(([icon, t, d], i) => {
        const Icon = { Newspaper, Handshake, ImageIcon, Camera: ImageIcon }[icon] || Newspaper;
        return (
          <Reveal key={t} delay={i}>
            <div className="mkt-card rounded-2xl p-6 h-full">
              <Icon size={24} className="text-dr-yellow mb-5" strokeWidth={2} />
              <h3 className="font-hero uppercase text-lg mb-2">{t}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{d}</p>
            </div>
          </Reveal>
        );
      })}
    </div>
  </Section>
);

/* ============ 12. DOĞUM GÜNÜ + MESAJ + BAŞVURU ============ */
const StripSection = () => (
  <Section id="strip" className="bg-dr-ink-1/30">
    <div className="grid md:grid-cols-3 gap-6">
      {[
        [Cake, "Doğum Günü Takibi", "30 günlük ileriye dönük widget. 'Yusuf 3 gün sonra' → tek tık AI görseli üret."],
        [MessageCircle, "İletişim Mesajları", "Web sitenizden gelen mesajlar — okunmamış/okundu durumu."],
        [GraduationCap, "Akademi Başvuruları", "Panelde yönetim — kabul / ret / beklemede durumu."],
      ].map(([Icon, t, d], i) => (
        <Reveal key={t} delay={i}>
          <div className="flex items-start gap-4 p-6 mkt-card rounded-2xl h-full">
            <Icon size={28} className="text-dr-yellow shrink-0 mt-1" strokeWidth={2} />
            <div>
              <h4 className="font-hero uppercase text-lg mb-1">{t}</h4>
              <p className="text-sm text-white/60 leading-relaxed">{d}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

/* ============ 13. KURUMSAL WEB SİTESİ ============ */
const WEB_PAGES = [
  "🏟️ Anasayfa — hero slider + yaklaşan maç geri sayımı + son haberler",
  "📅 Maçlar — fikstür + sonuçlar (otomatik)",
  "📊 Puan Durumu — canlı tablo",
  "👥 Kadro — A Takım + Akademi (fotoğraflı profiller)",
  "👔 Teknik Kadro — antrenör, yardımcılar",
  "📰 Haberler — kategori + sayfalama",
  "🎓 Akademi — grup tanıtımı + başvuru formu",
  "⭐ Sponsorlar — tier'lı galeri",
  "📸 Galeri — takım fotoğrafları",
  "📞 İletişim — form + harita + telefon",
];
const WEB_TECH = [
  "Özel domain (livanespor.org gibi) + SSL",
  "%100 mobil responsive",
  "<2sn açılış · Lighthouse 90+",
  "Kulüp marka renkleri (panel'den ayar)",
  "Dark / Light mod (ziyaretçi seçer)",
  "SEO dostu (Google 1. sırada)",
  "Open Graph sosyal paylaşım",
  "Taraftar iletişim formu",
  "Akademi online başvuru",
  "Sitemap + robots.txt otomatik",
  "Panel'de ne değiştirirsen siteye anında yansır",
];
const WebsiteSection = () => (
  <Section id="web-sitesi" className="overflow-hidden">
    <div className="absolute top-1/3 -right-40 w-[600px] h-[600px] bg-dr-yellow/10 blur-[140px] rounded-full -z-10" />
    <div className="max-w-4xl mb-14">
      <SectionNumber n={11} label="KURUMSAL WEB SİTESİ" />
      <Reveal>
        <h2 className="mkt-h2 mb-6">
          SADECE PANEL Mİ? <span className="text-dr-risk">HAYIR.</span><br />
          <span className="text-dr-yellow">KENDİ KURUMSAL</span><br />
          WEB SİTENİZ DE VAR.
        </h2>
      </Reveal>
      <Reveal delay={1}>
        <p className="text-lg text-white/70 max-w-3xl leading-relaxed">
          Bizimle anlaşan her kulübe, profesyonel bir <strong className="text-white">kurumsal futbol kulübü web sitesi</strong> teslim ederiz.
          Başkan her paylaşımda <strong className="text-dr-yellow">kulubunuz.com</strong> yazar — prestij basamak basamak yükselir.
        </p>
      </Reveal>
    </div>

    <Reveal delay={1}>
      <div className="relative mb-16 rounded-3xl overflow-hidden border border-white/10">
        <SmartImg src={`${IMG}/three-device-website.png`} alt="Kulüp Web Sitesi — 3 cihaz mockup" className="w-full" />
        <div className="absolute bottom-6 left-6 flex gap-2">
          <span className="px-3 py-1.5 rounded-full bg-dr-black/80 backdrop-blur border border-dr-yellow/30 font-over text-dr-yellow">DESKTOP</span>
          <span className="px-3 py-1.5 rounded-full bg-dr-black/80 backdrop-blur border border-dr-yellow/30 font-over text-dr-yellow">TABLET</span>
          <span className="px-3 py-1.5 rounded-full bg-dr-black/80 backdrop-blur border border-dr-yellow/30 font-over text-dr-yellow">MOBILE</span>
        </div>
      </div>
    </Reveal>

    <div className="grid lg:grid-cols-2 gap-10">
      <Reveal>
        <div className="mkt-card rounded-2xl p-8">
          <div className="font-over text-dr-yellow mb-4">ZİYARETÇİNİN GÖRECEĞİ SAYFALAR</div>
          <ul className="space-y-2.5">
            {WEB_PAGES.map((p) => (
              <li key={p} className="text-sm text-white/80 leading-relaxed">{p}</li>
            ))}
          </ul>
        </div>
      </Reveal>
      <Reveal delay={1}>
        <div className="mkt-card rounded-2xl p-8">
          <div className="font-over text-dr-yellow mb-4">TEKNİK ÖZELLİKLER</div>
          <ul className="space-y-2.5">
            {WEB_TECH.map((t) => (
              <li key={t} className="flex gap-2.5 text-sm text-white/80">
                <Check size={16} className="text-dr-yellow shrink-0 mt-0.5" strokeWidth={3} /> {t}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>

    <Reveal delay={2}>
      <div className="text-center mt-12">
        <a
          href="https://livanespor.org"
          target="_blank"
          rel="noopener noreferrer"
          className="mkt-btn-primary"
          data-testid="example-site-link"
        >
          Örnek: livanespor.org <ArrowUpRight size={18} strokeWidth={3} />
        </a>
      </div>
    </Reveal>
  </Section>
);

/* ============ 14. TEKNİK GÜÇ ============ */
const TECH_BADGES = [
  [Brain, "DR AI Engine", "OpenAI ChatGPT, Google Gemini, Anthropic Claude gibi dünyanın en iyi yapay zeka modellerini harmanlayarak DR AI Engine sistemine güç veriyor ve size en doğru sonuçları sağlıyoruz."],
  [ImageIcon, "DR AI Image 2", "HD görsel üretim · 1024×1024 · Türkçe uyumlu"],
  [RefreshCw, "Lig Verisi Entegrasyonu", "Canlı veri · APScheduler · Pazar 00:00"],
  [Shield, "Rol Bazlı Güvenlik", "Süper Admin / Yönetici / Medya ayrımı"],
  [Cloud, "Bulut Altyapı", "%99.9 uptime · günlük yedek · SSL"],
  [Zap, "Hot Reload", "Değişiklik 1 saniyede canlıya yansır"],
];
const TechSection = () => (
  <Section id="tech">
    <div className="max-w-4xl mb-12">
      <SectionNumber n={12} label="TEKNİK GÜÇ" />
      <Reveal>
        <h2 className="mkt-h2 mb-6">
          KALBİNDE <span className="text-dr-yellow">DÜNYANIN</span><br />
          EN İYİ TEKNOLOJİSİ
        </h2>
      </Reveal>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {TECH_BADGES.map(([Icon, t, d], i) => (
        <Reveal key={t} delay={i % 3}>
          <div className="mkt-card rounded-xl p-6">
            <Icon size={22} className="text-dr-yellow mb-4" strokeWidth={2} />
            <div className="font-hero text-lg uppercase mb-1.5">{t}</div>
            <div className="text-sm text-white/60 leading-relaxed">{d}</div>
          </div>
        </Reveal>
      ))}
    </div>

    <Reveal delay={2}>
      <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6 p-8 lg:p-12 rounded-3xl bg-gradient-to-br from-dr-yellow/10 via-transparent to-transparent border border-dr-yellow/20">
        {[
          ["20", "+ modül"],
          ["9", " AI şablon"],
          ["6000", "+ karakter rapor"],
          ["48", " saat kurulum"],
        ].map(([n, u]) => (
          <div key={u} className="text-center">
            <div className="font-hero text-5xl md:text-6xl text-dr-yellow">
              <Counter to={parseInt(n.replace(/\D/g, ""))} />
            </div>
            <div className="font-over text-white/60 mt-2">{u}</div>
          </div>
        ))}
      </div>
    </Reveal>
  </Section>
);

/* ============ 15. KULLANIM SENARYOSU ============ */
const TIMELINE = [
  ["09:30", "Başkan paneli açar.", "Dashboard'da: 'Elbeyli Üzümspor maçı için rapor hazır mı?' kartı görünür."],
  ["09:31", "'Analizi Oluştur' (1 kredi)", "AI 30 saniye bekler, motor çalışır."],
  ["09:32", "6000 kelimelik analiz çıkar.", "'Volkan Depe 33 gol / 23 maç' uyarısı kırmızı flaşlar."],
  ["09:35", "Başkan raporu antrenöre WhatsApp.", "Taktik şekillenir."],
  ["13:45", "Sahaya çıkılır. Taktik uygulanır.", "— "],
  ["15:50", "Skor 2-1 bizim lehimize.", "Sosyal medya sorumlusu panele girer."],
  ["15:51", "'Maç Sonucu' şablonu seç → '2-1 Galibiyet'.", "AI görsel üretir, DR AI FUTBOL imzalı."],
  ["15:52", "Instagram'da paylaşır.", "Hafta sonu takipçi +180."],
  ["18:00", "Yönetici maliyeye girer, ₺1.500 müsabaka gideri.", "— "],
  ["18:05", "Dashboard: Bu ay +₺11.500 · Geçen ay +₺9.200.", "BAŞKAN gülümser."],
];
const ScenarioSection = () => (
  <Section id="scenario" className="bg-dr-ink-1/30">
    <div className="max-w-4xl mb-14">
      <SectionNumber n={13} label="BİR GÜNDE NELER OLUR" />
      <Reveal>
        <h2 className="mkt-h2 mb-6">
          BİR <span className="text-dr-yellow">CUMARTESİ</span>,<br />
          SAAT <span className="text-dr-yellow">14:00</span>
        </h2>
      </Reveal>
    </div>
    <div className="relative max-w-3xl mx-auto">
      <div className="absolute left-[100px] top-0 bottom-0 w-px bg-gradient-to-b from-dr-yellow/60 via-dr-yellow/20 to-transparent hidden md:block" />
      <div className="space-y-8">
        {TIMELINE.map(([time, head, sub], i) => (
          <Reveal key={i} delay={i % 3}>
            <div className="flex gap-6 items-start">
              <div className="font-hero text-dr-yellow text-3xl md:text-4xl w-[100px] shrink-0 tabular-nums">{time}</div>
              <div className="relative flex-1">
                <div className="absolute -left-[26px] top-3 w-3 h-3 rounded-full bg-dr-yellow hidden md:block" />
                <div className="font-semibold text-white mb-1">{head}</div>
                {sub !== "— " && <div className="text-white/60 text-sm leading-relaxed">{sub}</div>}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

/* ============ 16. SSS ============ */
const FAQ = [
  ["Kurulum ne kadar sürer?", "48 saat. Size özel domain + panel hazır olur. Veri taşıma dahil."],
  ["Veri girişini kim yapar?", "Kulübünüzün yöneticisi (eğitim veriyoruz) ya da biz taşırız (Pro pakette ücretsiz)."],
  ["Kredi sistemi tam olarak nedir?", "Sistem içerisindeki bütün özellikler yapay zeka tarafından oluşturulur ve bundan dolayı kredi harcar. Mesela maç günü paylaşımı yapmak için görsel hazırlamak istediğinizde hesabınızdan 1 kredi düşecektir. Starter pakette 1 aylık 25 kredi, Plus pakette 1 aylık 75 kredi ve Pro pakette ise 1 aylık 250 kredi sunulmaktadır."],
  ["Kulübümüzün web sitesi de olmuş oluyor mu?", "Evet. Profesyonel kurumsal kulübünüzün renklerine ve ruhuna uygun benzersiz bir web sitesi sahibi olmuş oluyorsunuz."],
  ["Lig verileri doğru mu çekilir?", "Evet — her Pazar gece 00:00 otomatik + istediğiniz an manuel sync."],
  ["Web sitesi ayrı mı satılıyor?", "HAYIR — panel + web sitesi tek paket. Panel'de ne değiştirirseniz siteye anında yansır."],
  ["Mobil uygulama var mı?", "Panel tam responsive; mobil tarayıcıdan mükemmel çalışır. PWA desteği var, ana ekrana kısayol eklenir."],
  ["Birden fazla yönetici olabilir mi?", "Evet — Süper Admin + sınırsız yönetici hesabı. Rol bazlı yetki ayrımı."],
  ["Veri yedekleme?", "Günlük otomatik bulut yedek. Son 30 gün geri yüklenebilir."],
  ["Sözleşme süresi?", "1 yıllık. Memnun kalmazsanız 30 gün içinde %100 iade garantisi."],
  ["Benzer sistemden farkı?", "DR AI FUTBOL Türkiye amatör/profesyonel kulüp gerçeğine göre tasarlandı. Global SaaS'lar bunu yapamaz."],
];
const FAQSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <Section id="sss">
      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <SectionNumber n={14} label="SIKÇA SORULAN SORULAR" />
          <Reveal>
            <h2 className="mkt-h2 mb-6">
              AKLINIZDA<br />
              <span className="text-dr-yellow">SORU KALMASIN</span>
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="text-white/60 leading-relaxed">
              Başka bir sorunuz varsa, <Link to="/iletisim" className="text-dr-yellow underline">iletişime geçin</Link>.
              Ücretsiz üye olun, kulübünüzü aynı gün yönetmeye başlayın.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-8 space-y-3">
          {FAQ.map(([q, a], i) => (
            <Reveal key={q} delay={i % 4}>
              <div className="mkt-card rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.03] transition"
                  data-testid={`faq-${i}`}
                >
                  <span className="font-semibold text-white text-[15px]">{q}</span>
                  <ChevronDown size={18} className={`text-dr-yellow shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} strokeWidth={2.5} />
                </button>
                <AnimatePresence>
                  {open === i && (
                    <motion.div
                      initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 pb-5 text-white/70 text-[14px] leading-relaxed">{a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
};

/* ============ 17. FINAL CTA ============ */
const FinalCTA = () => {
  const [form, setForm] = useState({ ad_soyad: "", kulup_adi: "", telefon: "", email: "", rol: "", mesaj: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, kaynak: "landing-final-cta" }),
      });
      if (!res.ok) throw new Error("Gönderilemedi");
      setSent(true);
    } catch (e2) {
      setErr("Hata: " + e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="final-cta">
      <div className="relative rounded-[32px] overflow-hidden bg-gradient-to-br from-dr-yellow via-dr-yellow-hi to-dr-yellow p-10 md:p-16 lg:p-20 text-dr-black mkt-grain">
        <div className="relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-over text-dr-black/70 mb-6">ÜCRETSİZ ÜYELİK · KURULUM YOK</div>
            <h2 className="font-hero uppercase text-4xl md:text-6xl leading-[0.95] mb-6 text-dr-black">
              KULÜBÜNÜZ BU SİSTEMİ<br />
              <span className="underline decoration-dr-black decoration-4">HAK EDİYOR</span>
            </h2>
            <p className="text-dr-black/80 text-lg leading-relaxed mb-8 max-w-md">
              Panelin tüm özelliklerini ekranınızdan birlikte gezeriz. Size uygun paket önerilir.
              Söz — <strong>satış baskısı yok.</strong>
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://wa.me/905437934101" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-dr-black text-dr-yellow font-hero uppercase text-sm hover:bg-dr-ink-1 transition"
                data-testid="final-whatsapp"
              >
                <MessageCircle size={18} /> WhatsApp'tan Yaz
              </a>
              <a href="tel:+905437934101" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-dr-black/30 text-dr-black font-hero uppercase text-sm hover:bg-dr-black/10 transition"
                data-testid="final-call"
              >
                <Phone size={18} /> +90 543 793 41 01
              </a>
            </div>
          </div>

          <div className="bg-dr-black rounded-2xl p-7 md:p-8 text-white">
            {sent ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 rounded-full bg-dr-yellow text-dr-black flex items-center justify-center mx-auto mb-4">
                  <Check size={26} strokeWidth={3} />
                </div>
                <h3 className="font-hero text-2xl uppercase mb-2">Talebiniz Alındı</h3>
                <p className="text-white/60 text-sm">24 saat içinde arayacağız.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="final-form">
                <h3 className="font-hero text-2xl uppercase mb-4">Ücretsiz Üye Ol</h3>
                {[
                  ["ad_soyad", "Ad Soyad", "text", true],
                  ["kulup_adi", "Kulüp Adı", "text", true],
                  ["telefon", "Telefon (WhatsApp)", "tel", true],
                  ["email", "E-posta (opsiyonel)", "email", false],
                  ["rol", "Pozisyon (örn: Başkan, Genel Menajer)", "text", false],
                ].map(([name, ph, type, req]) => (
                  <input
                    key={name}
                    type={type}
                    required={req}
                    placeholder={ph}
                    value={form[name]}
                    onChange={(e) => setForm({ ...form, [name]: e.target.value })}
                    className="w-full bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
                    data-testid={`final-input-${name}`}
                  />
                ))}
                <textarea
                  rows={2}
                  placeholder="Kısa not (opsiyonel)"
                  value={form.mesaj}
                  onChange={(e) => setForm({ ...form, mesaj: e.target.value })}
                  className="w-full bg-dr-ink-2 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-dr-yellow focus:outline-none"
                  data-testid="final-input-mesaj"
                />
                {err && <div className="text-dr-risk text-sm">{err}</div>}
                <button
                  disabled={loading}
                  className="mkt-btn-primary w-full justify-center !py-3.5 disabled:opacity-60"
                  data-testid="final-submit"
                >
                  {loading ? "Gönderiliyor..." : "Gönder"} {!loading && <ArrowRight size={18} strokeWidth={3} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ============ MAIN PAGE ============ */
export default function LandingPage() {
  // Title/meta handled by <MarketingSEO /> via react-helmet-async (set in HomeRouter).
  return (
    <MarketingLayout>
      <Hero />
      <BenefitsSection />
      <DashboardSection />
      <AIAnalysisSection />
      <TemplatesSection />
      <KasaSection />
      <ContractSection />
      <TrainingSection />
      <AcademyMackolikSection />
      <MiniGridSection />
      <StripSection />
      <WebsiteSection />
      <TechSection />
      <ScenarioSection />
      <FAQSection />
      <FinalCTA />
    </MarketingLayout>
  );
}


